import { Book } from 'epubjs';
import { RenditionOptions } from 'epubjs/types/rendition';
import HeadingBasedIframeView from './epubJs/managers/views/headingbasediframe';
import HeadingBasedContinuousViewManager from './epubJs/managers/headingbased';
import FlipperManager from './epubJs/managers/flipper';
import FlippingIframeView from './epubJs/managers/views/flippingiframe';
import SliderManager from './epubJs/managers/slider';
import SlidingIframeView from './epubJs/managers/views/slidingiframe';
import ReflowableSliderManager from './epubJs/managers/reflowableslider';
import FrogSafeIframeView from './epubJs/managers/views/frogsafeiframe';

/**
 * The minimum reader width at which we display two pages as spread by default.
 * Below this width the reader will display only one page by default.
 * Users can still override this preference via the page display toggle.
 * Set to 667px to support iPhone SE (2nd/3rd gen) in landscape mode.
 */
export const MIN_SPREAD_WIDTH = 667;
export const viewerElementId = 'viewer';

export const getViewerElementWidth = () => {
  const viewer = getRenderedBookViewerElement();
  if (viewer) {
    return viewer.offsetWidth;
  }
  return window.innerWidth;
};

/*
 * Check if the width of the viewer is enough to display spreads.
 *
 * On touch devices, we always display on page in portrait mode.
 * In landscape mode, we display spreads if the width is enough.
 */
export const hasWidthForSpreads = (isTouchDevice: boolean) => {
  const isScreenWideEnough = getViewerElementWidth() >= MIN_SPREAD_WIDTH;

  if (isTouchDevice) {
    return window.innerWidth > window.innerHeight && isScreenWideEnough;
  }

  return isScreenWideEnough;
};

/**
 * Which manager/view pair the book is rendered with.
 *
 * - `page-turn`  → FlipperManager + FlippingIframeView (curl animation)
 * - `page-slide` → SliderManager + SlidingIframeView
 * - `reflowable-page-slide` → ReflowableSliderManager with the default view (FrogSafeIframeView stays for Frog books)
 * - `none`       → whatever renderOptions already decided (EPUBjs default, or FrogSafeIframeView)
 */
export type RenderingStrategy = 'page-turn' | 'page-slide' | 'reflowable-page-slide' | 'none';

/**
 * The single source of truth for which animated manager/view pair a book gets.
 *
 * This MUST be the only place the decision is made. Callers that need to know whether the page turn animation is
 * actually active (e.g. to set `isPageTurnAnimationEnabled`, which touch-controls uses to decide whether the view
 * has FlippingIframeView's methods) have to ask this function rather than re-deriving the condition — when the two
 * derivations drifted apart, touch handlers called FlippingIframeView methods on a SlidingIframeView and threw on
 * every single touch.
 */
export const resolveRenderingStrategy = ({
  pageTurnAnimationRequested,
  pageSlideAnimationRequested,
  isPrePaginated,
  isContinuous,
  hasWidthForSpread,
  hasSpreadNone,
  userPrefersSinglePage,
}: {
  pageTurnAnimationRequested: boolean;
  pageSlideAnimationRequested: boolean;
  isPrePaginated: boolean;
  isContinuous: boolean;
  hasWidthForSpread: boolean;
  hasSpreadNone: boolean;
  userPrefersSinglePage: boolean;
}): RenderingStrategy => {
  // Continuous books use the heading based manager, no page animation applies.
  if (isContinuous) {
    return 'none';
  }

  // Page turn (curl) animation - requires a pre-paginated book displayed as a spread.
  // Not used for books with spread=none or when user prefers single-page mode.
  if (pageTurnAnimationRequested && isPrePaginated && hasWidthForSpread && !hasSpreadNone && !userPrefersSinglePage) {
    return 'page-turn';
  }

  // Slide animation for PRE-PAGINATED books only. SliderManager is designed for fixed-layout pages and doesn't
  // work with reflowable content - for reflowable books single-page mode is handled by spread: 'none'.
  // Used when slide animation is explicitly requested, when the book dictates single-page mode via spread=none,
  // or when the user selected single-page mode.
  if (isPrePaginated && (pageSlideAnimationRequested || hasSpreadNone || userPrefersSinglePage)) {
    return 'page-slide';
  }

  if (!isPrePaginated && pageSlideAnimationRequested) {
    return 'reflowable-page-slide';
  }

  return 'none';
};

export const renderBook = (
  book: Book,
  infiniteScrollBook: boolean,
  renderingStrategy: RenderingStrategy,
  isTouch: boolean,
  isFrogBook: boolean,
  platform: {
    // Could not import vuetify's DisplayPlatform here, so I just copied the relevant parts
    android: boolean;
    ios: boolean;
    cordova: boolean;
    electron: boolean;
    chrome: boolean;
    edge: boolean;
    firefox: boolean;
    opera: boolean;
    win: boolean;
    mac: boolean;
    linux: boolean;
    touch: boolean;
    ssr: boolean;
  },
  hasSpreadNone = false, // New parameter for books with rendition:spread=none
  userPrefersSinglePage = false, // User preference from settings (works for both pre-paginated and reflowable)
  currentHasWidthForSpreads?: boolean, // PASS from store to use pre-computed value
) => {
  // Use passed value or compute it (for backward compatibility)
  const hasWidthForSpread = currentHasWidthForSpreads ?? hasWidthForSpreads(isTouch);
  // Determine if book should use single-page mode
  // Reasons to use single-page mode:
  // 1. Book has rendition:spread=none metadata
  // 2. Screen is too narrow for spreads
  // 3. User has chosen single-page mode in settings (works for both pre-paginated AND reflowable books)
  const shouldUseSinglePageMode = hasSpreadNone || !hasWidthForSpread || userPrefersSinglePage;

  const renderOptions: RenditionOptions = {
    width: '100%',
    height: '100%',
    // Use 'none' for single-page mode, 'always' for spread mode
    // This applies to both pre-paginated and reflowable books
    spread: shouldUseSinglePageMode ? 'none' : 'always',
    minSpreadWidth: MIN_SPREAD_WIDTH,
    resizeOnOrientationChange: true,
    allowScriptedContent: true,
  };

  /*
  This is a hack to know in the IframeView class is this is a frog book, as we can not pass parameters to the iframe view class
   */
  // @ts-ignore
  window.isFrogBook = isFrogBook;

  if (isFrogBook) {
    /*
     This is a hack to make the frog book work on Firefox and Safari

     Check https://iltservices.atlassian.net/browse/IT-2701 for more information
     */
    // @ts-ignore
    window.isFirefox = platform.firefox;
    // @ts-ignore
    window.thisCouldBeSafari =
      (platform.ios || platform.mac) && !platform.firefox && !platform.chrome && !platform.edge && !platform.opera;

    renderOptions.view = FrogSafeIframeView;
  }

  const infiniteScrollBookOptions = infiniteScrollBook
    ? {
        flow: 'scrolled',
        view: HeadingBasedIframeView,
        spread: 'none',
        manager: HeadingBasedContinuousViewManager,
      }
    : {};

  // Animation options are mutually exclusive - only one should be applied.
  // The decision itself lives in resolveRenderingStrategy() so that callers who need to know which view class was
  // used can ask the same function instead of re-deriving the condition.
  let animationOptions = {};

  if (renderingStrategy === 'page-turn') {
    animationOptions = {
      flow: 'paginated',
      manager: FlipperManager,
      view: FlippingIframeView,
    };
  } else if (renderingStrategy === 'page-slide') {
    animationOptions = {
      flow: 'paginated',
      manager: SliderManager,
      view: SlidingIframeView,
    };
  } else if (renderingStrategy === 'reflowable-page-slide') {
    animationOptions = {
      manager: ReflowableSliderManager,
    };
  }

  const viewerElement = document.getElementById(viewerElementId);
  if (viewerElement) {
    viewerElement.innerHTML = '';
  }

  book.renderTo(viewerElementId, {
    ...renderOptions,
    ...infiniteScrollBookOptions,
    ...animationOptions,
  });
};

export const getEpubContainerElement = (): HTMLElement | null => document.querySelector('.epub-container');

export const getRenderedBookViewerElement = () => document.getElementById(viewerElementId);
